import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';
import { Button } from '@mui/material';
import { West as WestIcon } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { SignatureCaptureDialog } from '@/components/shared/SignatureCaptureDialog';
import { extractErrorMessage, normalizeDisplayValue } from '../workflowUtils';
import { useWorkflowSignatureCapture } from '../useWorkflowSignatureCapture';
import { TurnInConfirmationDialog } from './TurnInConfirmationDialog';
import { TurnInPlannerTable } from './TurnInPlannerTable';
import { TurnInPlannerToolbar } from './TurnInPlannerToolbar';
import { TurnInCustomerSummaryModal } from './TurnInCustomerSummaryModal';
import { TurnInSidebarSummary } from './TurnInSidebarSummary';
import type {
  RecoverableState,
  TurnInAssignmentCounts,
  TurnInConditionFilter,
  TurnInDisposition,
  TurnInIssuedItem,
  TurnInReceiptTypeFilter,
  TurnInRow,
  TurnInSummaryItem,
} from './types';
import { usePHRItems, useTHRItems } from '@/hooks/queries/iem/useIEMQueries';
import { iemService } from '@/services/iem';
import { queryKeys } from '@/lib/queryKeys';
import { useNotifications } from '@/stores/useNotificationStore';
import { groupExchangeIssuedItems } from '@/utils/iemIssuedItems';
import { useMemberEmail } from '@/hooks/useMemberEmail';
import {
  formatCurrency,
  generateChangeDocument,
  type ChangeDocumentLineItem,
} from '@/utils/generateChangeDocument';
import { preOpenWindow } from '@/utils/pdfHelpers';
import { EXCLUDE_PNP_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { documentService } from '@/services/documents';
import type { IEMCustomer } from '@/services/iem/types';
import { logger } from '@equip/utils/logger';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const log = logger.child({ component: 'TurnInWorkflow' });

const RECOVERABLE_DISPLAY: Record<
  RecoverableState,
  Pick<TurnInRow, 'condition' | 'conditionTone'>
> = {
  recoverable: {
    condition: 'Recoverable',
    conditionTone: 'success',
  },
  nonRecoverable: {
    condition: 'Non-recoverable',
    conditionTone: 'warning',
  },
  unknown: {
    condition: 'Condition unknown',
    conditionTone: 'default',
  },
};

const getRecoverableState = (recoverable: boolean | null | undefined): RecoverableState => {
  if (recoverable === true) return 'recoverable';
  if (recoverable === false) return 'nonRecoverable';
  return 'unknown';
};

const createEmptyCounts = (): TurnInAssignmentCounts => ({
  recoverable: 0,
  non_recoverable: 0,
  cleared: 0,
});

const buildSummaryItem = (item: TurnInIssuedItem): TurnInSummaryItem => ({
  id: item.handReceiptId,
  name: item.name,
  lin: item.lin && item.lin !== '—' ? item.lin : null,
  kitName: item.kitName ?? null,
  source: item.receiptType ?? '—',
  asset_id: item.assetId ?? undefined,
});

const getThrRecoverableState = (
  recoverable: boolean | null | undefined,
  nonrecoverableReason?: string | null,
): RecoverableState => {
  const base = getRecoverableState(recoverable);
  if (base !== 'unknown') return base;
  const hasNonRecoverableReason = Boolean(nonrecoverableReason?.trim());
  return hasNonRecoverableReason ? 'nonRecoverable' : 'recoverable';
};

export interface TurnInWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  unitName?: string;
  memberName?: string;
  customerId?: string;
  memberEmail?: string;
  /** Full customer data for Change Document generation */
  customer?: IEMCustomer;
  onCompleted?: (result: {
    memberId: string;
    returnedCount: number;
    closedReceiptIds: string[];
    recoverableCount: number;
    nonRecoverableCount: number;
    clearedCount: number;
    transactionLogIds: string[];
  }) => void;
}

export const TurnInWorkflowModal = ({
  open,
  onClose,
  unitName,
  memberName,
  customerId,
  memberEmail: memberEmailProp,
  customer,
  onCompleted,
}: TurnInWorkflowModalProps) => {
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const iemLabels = iemConfig.features.front_window;
  const person = usePersonLabels('front_window');
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { data: phrData = [], isLoading: phrLoading } = usePHRItems(
    customerId || '',
    EXCLUDE_PNP_TYPE_KEEP_NULL,
  );
  const { data: thrData = [], isLoading: thrLoading } = useTHRItems(
    customerId || '',
    EXCLUDE_PNP_TYPE_KEEP_NULL,
  );
  const { email: memberEmail } = useMemberEmail(customerId, memberEmailProp);

  const isLoading = phrLoading || thrLoading;

  const issuedItems = useMemo<TurnInIssuedItem[]>(() => {
    if (!customerId) {
      return [];
    }

    const toIssuedItem = (
      item: (typeof phrData)[number] | (typeof thrData)[number],
      receiptType: 'PHR' | 'THR',
      recoverableOverride?: RecoverableState,
    ): TurnInIssuedItem => {
      const recoverableState = recoverableOverride ?? getRecoverableState(item.recoverable);
      const { condition, conditionTone } = RECOVERABLE_DISPLAY[recoverableState];

      return {
        id: item.id,
        handReceiptId: item.id,
        assetId: item.asset_id ?? undefined,
        lin: item.lin ?? '—',
        nsn: normalizeDisplayValue(item.nsn),
        name: item.item,
        issuedQty: 1,
        issuedAt: normalizeDisplayValue(item.issueDate),
        issueDate: normalizeDisplayValue(item.issueDate),
        dueDate: normalizeDisplayValue('dueDate' in item ? item.dueDate : '—'),
        receiptType,
        baseNsn: null,
        recoverableState,
        condition,
        conditionTone,
        expirationDate: item.expirationDate ?? null,        kitName: item.ie_kit_name ?? null,      } satisfies TurnInIssuedItem;
    };

    const phrIssued = phrData
      .filter((item) => item.status?.toLowerCase() !== 'closed')
      .map((item) => toIssuedItem(item, 'PHR'));
    const thrIssued = thrData
      .filter((item) => item.status?.toLowerCase() !== 'closed')
      .map((item) =>
        toIssuedItem(
          item,
          'THR',
          getThrRecoverableState(item.recoverable, item.nonrecoverable_reason),
        ),
      );

    return [...thrIssued, ...phrIssued];
  }, [customerId, phrData, thrData]);

  const issuedItemsById = useMemo(() => {
    const map = new Map<string, TurnInIssuedItem>();
    issuedItems.forEach((item) => {
      map.set(item.handReceiptId, item);
    });
    return map;
  }, [issuedItems]);

  const turnInRowsData = useMemo<TurnInRow[]>(() => {
    if (issuedItems.length === 0) {
      return [];
    }

    const grouped = groupExchangeIssuedItems(issuedItems, { splitByReceiptType: true });
    const rows: TurnInRow[] = [];

    grouped.forEach((group) => {
      const itemsByRecoverable = new Map<RecoverableState, TurnInIssuedItem[]>();
      (group.items as TurnInIssuedItem[]).forEach((item) => {
        const bucket = itemsByRecoverable.get(item.recoverableState) ?? [];
        bucket.push(item);
        itemsByRecoverable.set(item.recoverableState, bucket);
      });

      itemsByRecoverable.forEach((items, recoverableState) => {
        const sortedItems = [...items].sort((a, b) =>
          a.handReceiptId.localeCompare(b.handReceiptId),
        );
        const firstItem = sortedItems[0];
        if (!firstItem) {
          return;
        }
        const { condition, conditionTone } = RECOVERABLE_DISPLAY[recoverableState];
        rows.push({
          id: `${group.id}::${recoverableState}`,
          item: group.name,
          lin: group.lin,
          nsn: group.nsn,
          receiptType: (firstItem.receiptType ?? 'PHR') as 'PHR' | 'THR',
          condition,
          conditionTone,
          recoverableState,
          totalQty: sortedItems.length,
          itemIds: sortedItems.map((item) => item.handReceiptId),
          expirationDate: firstItem.expirationDate ?? null,
        });
      });
    });

    return rows.sort((a, b) => {
      if (a.receiptType !== b.receiptType) {
        return a.receiptType === 'THR' ? -1 : 1;
      }
      return a.item.localeCompare(b.item);
    });
  }, [issuedItems]);

  const [itemDispositions, setItemDispositions] = useState<
    Record<string, TurnInDisposition | undefined>
  >({});
  const [conditionFilter, setConditionFilter] = useState<TurnInConditionFilter>('all');
  const [receiptTypeFilter, setReceiptTypeFilter] = useState<TurnInReceiptTypeFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [signatureAcknowledged, setSignatureAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [customerSummaryOpen, setCustomerSummaryOpen] = useState(false);
  const workflowStartedAtRef = useRef(new Date().toISOString());
  const {
    capturedSignature,
    signatureCaptureOpen,
    signaturePreviewSrc,
    openSignatureCapture,
    closeSignatureCapture,
    saveSignature,
    clearSignature,
    resetSignatureCapture,
  } = useWorkflowSignatureCapture({
    signatureAcknowledged,
    setSignatureAcknowledged,
  });

  useEffect(() => {
    setItemDispositions((prev) => {
      const next: Record<string, TurnInDisposition | undefined> = {};
      let didChange = false;
      Object.entries(prev).forEach(([handReceiptId, disposition]) => {
        if (!issuedItemsById.has(handReceiptId)) {
          didChange = true;
          return;
        }
        next[handReceiptId] = disposition;
      });
      return didChange ? next : prev;
    });
  }, [issuedItemsById]);

  useEffect(() => {
    if (!open) {
      setItemDispositions({});
      setSearchTerm('');
      setConditionFilter('all');
      setReceiptTypeFilter('all');
      resetSignatureCapture();
      setIsSubmitting(false);
      setConfirmDialogOpen(false);
      setCustomerSummaryOpen(false);
    }
  }, [open, resetSignatureCapture]);

  useEffect(() => {
    setItemDispositions({});
  }, [customerId]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    let rows = turnInRowsData;

    rows = rows.filter((row) => {
      if (conditionFilter !== 'all' && row.recoverableState !== conditionFilter) {
        return false;
      }
      if (receiptTypeFilter !== 'all' && row.receiptType !== receiptTypeFilter) {
        return false;
      }
      if (normalizedSearch) {
        const nsn = row.nsn.toLowerCase();
        const haystack = `${row.item} ${row.lin} ${nsn}`.toLowerCase();
        return multiWordMatch(haystack, normalizedSearch);
      }
      return true;
    });

    return rows;
  }, [turnInRowsData, conditionFilter, receiptTypeFilter, normalizedSearch]);

  const rowAssignmentCounts = useMemo(() => {
    const map = new Map<string, TurnInAssignmentCounts>();
    turnInRowsData.forEach((row) => {
      const counts = createEmptyCounts();
      row.itemIds.forEach((itemId) => {
        const disposition = itemDispositions[itemId];
        if (disposition) {
          counts[disposition] += 1;
        }
      });
      map.set(row.id, counts);
    });
    return map;
  }, [turnInRowsData, itemDispositions]);

  const buildSummaryItems = useCallback(
    (disposition: TurnInDisposition) => {
      return Object.entries(itemDispositions)
        .filter(([, value]) => value === disposition)
        .map(([handReceiptId]) => issuedItemsById.get(handReceiptId))
        .filter((item): item is TurnInIssuedItem => Boolean(item))
        .map((item) => buildSummaryItem(item));
    },
    [itemDispositions, issuedItemsById],
  );

  const serviceableItems = useMemo(() => buildSummaryItems('recoverable'), [buildSummaryItems]);
  const nonServiceableItems = useMemo(
    () => buildSummaryItems('non_recoverable'),
    [buildSummaryItems],
  );
  const clearedItems = useMemo(() => buildSummaryItems('cleared'), [buildSummaryItems]);

  const hasItems = serviceableItems.length + nonServiceableItems.length + clearedItems.length > 0;

  const canClearNonRecoverables = useMemo(
    () =>
      turnInRowsData.some(
        (row) =>
          row.recoverableState === 'nonRecoverable' &&
          row.itemIds.some((itemId) => itemDispositions[itemId] !== 'cleared'),
      ),
    [turnInRowsData, itemDispositions],
  );

  const handleRemoveSummaryItem = (handReceiptId: string) => {
    setItemDispositions((prev) => ({ ...prev, [handReceiptId]: undefined }));
  };

  const handleAddAllocation = (rowId: string, disposition: TurnInDisposition) => {
    const row = turnInRowsData.find((r) => r.id === rowId);
    if (!row) return;

    // Find the first item in this row that doesn't have a disposition
    const firstUnassignedId = row.itemIds.find((id) => !itemDispositions[id]);

    if (firstUnassignedId) {
      setItemDispositions((prev) => ({
        ...prev,
        [firstUnassignedId]: disposition,
      }));
    }
  };

  const handleClearNonRecoverables = () => {
    try {
      const next = { ...itemDispositions };
      let hasChanges = false;

      turnInRowsData.forEach((row) => {
        if (row.recoverableState !== 'nonRecoverable') {
          return;
        }
        row.itemIds.forEach((id) => {
          if (!id) {
            return;
          }
          if (next[id] !== 'cleared') {
            next[id] = 'cleared';
            hasChanges = true;
          }
        });
      });

      if (hasChanges) {
        setItemDispositions(next);
      }
    } catch (error) {
      log.error('Failed to clear non-recoverable items', { error: String(error) });
      notifications.error(
        'System Error',
        'An unexpected error occurred while clearing items. Please try again.',
      );
    }
  };

  const handleCaptureSignature = () => {
    log.debug(`Capturing ${person.lower} signature`);
    openSignatureCapture();
  };

  const handleSkipSignature = () => {
    log.debug(`Skipping ${person.lower} signature`);
    setSignatureAcknowledged(true);
  };

  const handleSignatureSaved = (signatureBase64Png: string) => {
    saveSignature(signatureBase64Png);
    notifications.success('Signature captured', `${person.label} signature saved.`);
  };

  const unselectedRecoverableItems = useMemo(() => {
    return issuedItems.filter(
      (item) => item.recoverableState === 'recoverable' && !itemDispositions[item.handReceiptId],
    );
  }, [issuedItems, itemDispositions]);

  const proceedWithTurnIn = async () => {
    if (isSubmitting) return;

    if (!customerId) {
      notifications.error(
        'Unable to complete turn-in',
        `Select ${person.article} ${person.lower} before turning in equipment.`,
      );
      return;
    }

    const items = [
      ...serviceableItems.map((item) => ({
        handReceiptId: item.id,
        assetId: item.asset_id!,
        disposition: 'recoverable' as const,
      })),
      ...nonServiceableItems.map((item) => ({
        handReceiptId: item.id,
        assetId: item.asset_id!,
        disposition: 'non_recoverable' as const,
      })),
    ];

    const missingAsset = items.some((item) => !item.assetId);
    if (missingAsset) {
      notifications.error(
        'Missing asset details',
        'One or more selected items are missing asset information. Refresh and try again.',
      );
      return;
    }

    if (items.length === 0) {
      notifications.warning(
        'Turn-in cart is empty',
        'Select hand receipt items before completing turn-in.',
      );
      return;
    }

    setIsSubmitting(true);

    // Pre-open window synchronously while we have user-gesture context
    const changeDocWindow = preOpenWindow();

    try {
      const result = await iemService.completeTurnIn({
        memberId: customerId,
        items: items.map((item) => ({
          handReceiptId: item.handReceiptId,
          assetId: item.assetId!,
          disposition: item.disposition,
        })),
        documentPayload: capturedSignature
          ? {
              signatures: { individual: capturedSignature },
              transactionInfo: {
                timestamp: new Date().toISOString(),
              },
              workflow: 'turn-in',
            }
          : undefined,
        startedAt: workflowStartedAtRef.current,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['phr-items', customerId] }),
        queryClient.invalidateQueries({ queryKey: ['thr-items', customerId] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.bulkInventoryBin.list() }),
      ]);
      notifications.success(
        'Turn-in completed',
        `Processed ${items.length} hand receipt item${items.length === 1 ? '' : 's'}.`,
      );
      notifications.info(
        memberEmail
          ? `Email notification will be sent to ${memberEmail}.`
          : 'Email notification will be sent',
      );

      // Generate Change Document for the turn-in (best-effort)
      try {
        const allTurnedInIds = [
          ...serviceableItems.map((i) => i.id),
          ...nonServiceableItems.map((i) => i.id),
        ];

        if (allTurnedInIds.length > 0) {
          // Build a price lookup from the raw PHR/THR data so we can populate unit cost
          const priceLookup = new Map<string, number>();
          for (const item of [...phrData, ...thrData]) {
            const price = item.actualPrice ?? item.unitPrice ?? 0;
            if (price) priceLookup.set(item.id, price);
          }

          // Build remaining qty-on-hand per NSN/LIN key:
          // count all currently assigned items, then subtract the ones being turned in.
          const remainingQtyByKey = new Map<string, number>();
          for (const item of issuedItems) {
            const nsnKey = item.nsn === '—' ? '' : (item.nsn ?? '');
            const linKey = item.lin === '—' ? '' : (item.lin ?? '');
            const key = nsnKey || linKey || item.name;
            remainingQtyByKey.set(key, (remainingQtyByKey.get(key) ?? 0) + 1);
          }
          for (const id of allTurnedInIds) {
            const item = issuedItemsById.get(id);
            if (!item) continue;
            const nsnKey = item.nsn === '—' ? '' : (item.nsn ?? '');
            const linKey = item.lin === '—' ? '' : (item.lin ?? '');
            const key = nsnKey || linKey || item.name;
            remainingQtyByKey.set(key, (remainingQtyByKey.get(key) ?? 0) - 1);
          }

          const changeDocItems: ChangeDocumentLineItem[] = allTurnedInIds.map((handReceiptId) => {
            const issued = issuedItemsById.get(handReceiptId);
            const receiptType = issued?.receiptType ?? 'PHR';
            const recoverableState = issued?.recoverableState;
            const rNr =
              recoverableState === 'recoverable'
                ? 'R'
                : recoverableState === 'nonRecoverable'
                  ? 'NR'
                  : '';
            const qty = issued?.issuedQty ?? 1;
            const unitCost = priceLookup.get(handReceiptId) ?? 0;
            const nsnNorm = issued?.nsn === '—' ? '' : (issued?.nsn ?? '');
            const linNorm = issued?.lin === '—' ? '' : (issued?.lin ?? '');
            const itemKey = nsnNorm || linNorm || issued?.name || '';
            const remainingQty = Math.max(0, remainingQtyByKey.get(itemKey) ?? 0);
            return {
              qty_turn_in: String(qty),
              // Strip em-dash display values back to empty strings for the PDF
              lin: linNorm,
              nsn: nsnNorm,
              nomenclature: issued?.name || '',
              thr_phr: receiptType,
              qty_oh: String(remainingQty),
              unit_cost: unitCost ? formatCurrency(unitCost) : '',
              total_cost: unitCost ? formatCurrency(unitCost * qty) : '',
              r_nr: rNr,
            };
          });
          const turnInTxLogId = result.transactionLogIds?.[0];

          const changeDocResult = await generateChangeDocument({
            documentType: 'ChangeDocumentTurnIn',
            items: changeDocItems,
            name: (customer
              ? [customer.last_name, customer.first_name].filter(Boolean).join(', ').trim()
              : null) || memberName || '',
            individualId: customer?.individual_id,
            rank: customer?.rank,
            section: customer?.unit || unitName,
            documentNumber: result.documentNumber || '',
            transactionId: turnInTxLogId,
            signature: capturedSignature || undefined,
            preOpenedWindow: changeDocWindow,
          });

          // Link Change Document to transaction log for audit trail consistency
          if (changeDocResult?.documentEntityId && turnInTxLogId) {
            try {
              await documentService.linkDocumentToTransaction(
                changeDocResult.documentEntityId,
                turnInTxLogId,
              );
            } catch (linkError) {
              log.error('Failed to link Change Document to turn-in transaction', {
                error: String(linkError),
              });
            }
          }

          if (!changeDocResult) {
            notifications.warning('Document Notice', 'Change Document could not be generated.');
          }
        } else if (changeDocWindow && !changeDocWindow.closed) {
          changeDocWindow.close();
        }
      } catch (docError) {
        if (changeDocWindow && !changeDocWindow.closed) changeDocWindow.close();
        log.error('Failed to generate Change Document for turn-in', {
          error: String(docError),
        });
        notifications.warning('Document Notice', 'Change Document could not be generated.');
      }

      onCompleted?.({
        memberId: result.memberId,
        returnedCount: result.returnedCount,
        closedReceiptIds: result.closedReceiptIds,
        recoverableCount: result.recoverableCount,
        nonRecoverableCount: result.nonRecoverableCount,
        clearedCount: result.clearedCount,
        transactionLogIds: Array.isArray(result.transactionLogIds) ? result.transactionLogIds : [],
      });
      setItemDispositions({});
      resetSignatureCapture();
      onClose();
    } catch (error) {
      if (changeDocWindow && !changeDocWindow.closed) changeDocWindow.close();
      const message = extractErrorMessage(error, 'Unexpected error completing turn-in.');
      notifications.error('Failed to complete turn-in', message);
      log.error('Failed to complete turn-in', { error: String(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteTurnInClick = () => {
    if (unselectedRecoverableItems.length > 0) {
      setConfirmDialogOpen(true);
    } else {
      proceedWithTurnIn();
    }
  };

  const plannerToolbar = (
    <TurnInPlannerToolbar
      searchTerm={searchTerm}
      onSearchTermChange={(value) => setSearchTerm(value)}
      conditionFilter={conditionFilter}
      onConditionFilterChange={(nextFilter) => setConditionFilter(nextFilter)}
      receiptTypeFilter={receiptTypeFilter}
      onReceiptTypeFilterChange={(nextFilter) => setReceiptTypeFilter(nextFilter)}
      canClearNonRecoverables={canClearNonRecoverables}
      onClearNonRecoverables={handleClearNonRecoverables}
    />
  );

  const plannerContent = (
    <TurnInPlannerTable
      isLoading={isLoading}
      customerId={customerId}
      rows={filteredRows}
      rowAssignmentCounts={rowAssignmentCounts}
      onAddAllocation={handleAddAllocation}
    />
  );

  const sidebarContent = (
    <TurnInSidebarSummary
      serviceableItems={serviceableItems}
      nonServiceableItems={nonServiceableItems}
      hasItems={hasItems}
      signatureAcknowledged={signatureAcknowledged}
      signaturePreviewSrc={signaturePreviewSrc}
      isSubmitting={isSubmitting}
      onShowCustomerSummary={() => setCustomerSummaryOpen(true)}
      onRemoveItem={handleRemoveSummaryItem}
      onCaptureSignature={handleCaptureSignature}
      onSkipSignature={handleSkipSignature}
      onClearSignature={capturedSignature ? clearSignature : undefined}
      onCompleteTurnIn={handleCompleteTurnInClick}
    />
  );

  const subtitle = `${unitName} • ${memberName}`;

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={onClose}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Return to Front-Window
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={onClose}
        title={iemLabels.turn_in_workflow_title}
        subTitle={subtitle}
        disableFocusTrap={confirmDialogOpen}
        plannerTitle="Turn-In Planner"
        plannerToolbar={plannerToolbar}
        plannerContent={plannerContent}
        sidebarTitle="Return Cart Summary"
        sidebarContent={sidebarContent}
        closeLabel="Close turn-in workflow"
        closeButton={closeButton}
      />
      <SignatureCaptureDialog
        open={signatureCaptureOpen}
        title={`Capture ${person.label} Signature`}
        initialSignature={capturedSignature}
        onCancel={closeSignatureCapture}
        onSave={handleSignatureSaved}
      />
      <TurnInConfirmationDialog
        open={confirmDialogOpen}
        unselectedRecoverableCount={unselectedRecoverableItems.length}
        onCancel={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          setConfirmDialogOpen(false);
          proceedWithTurnIn();
        }}
      />
      <TurnInCustomerSummaryModal
        open={customerSummaryOpen}
        onClose={() => setCustomerSummaryOpen(false)}
        unitName={unitName}
        memberName={memberName}
        serviceableItems={serviceableItems}
        nonServiceableItems={nonServiceableItems}
      />
    </>
  );
};
